import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { MapPin, Pencil, Trash2, Plus } from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useLang } from "../../context/LangContext";
import AddressDialog from "../../components/AddressDialog";
import SubPageShell from "../../components/SubPageShell";

export default function Addresses() {
  const { user } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [addresses, setAddresses] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => api("/me/addresses", { auth: true }).then(setAddresses).catch(() => setAddresses([]));

  useEffect(() => {
    if (user === undefined) return;
    if (!user) { navigate("/login?next=/account/addresses"); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (!user) return null;

  const del = async (a) => {
    if (!window.confirm(`${t("tab_addresses")}: ${a.label || a.address}?`)) return;
    try {
      await api(`/me/addresses/${a.id}`, { method: "DELETE", auth: true });
      toast.success(t("addr_deleted"));
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <SubPageShell
      title={t("tab_addresses")}
      tid="addresses-page"
      actions={
        <button
          data-testid="address-add-button"
          onClick={() => { setEditing(null); setOpen(true); }}
          className="inline-flex items-center gap-1.5 rounded-full bg-teal-700 px-3.5 py-1.5 text-xs font-bold text-white transition hover:bg-teal-800 active:scale-95"
        >
          <Plus className="h-3.5 w-3.5" /> {t("add_address")}
        </button>
      }
    >
      {addresses === null ? (
        <p data-testid="addresses-loading" className="py-10 text-center text-sm text-slate-500">...</p>
      ) : addresses.length === 0 ? (
        <div data-testid="addresses-empty" className="mt-6 rounded-2xl border border-dashed border-slate-300 py-12 text-center">
          <p className="font-heading font-semibold text-slate-800">{t("no_addr")}</p>
          <p className="mt-1 text-sm text-slate-500">{t("no_addr_sub")}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {addresses.map((a) => (
            <div key={a.id} data-testid={`address-card-${a.id}`} className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-slate-100">
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-teal-600" />
                <span className="font-heading font-semibold text-slate-900">{a.label || a.name}</span>
                {a.is_default && (
                  <span className="rounded-full bg-teal-100 px-2 py-0.5 text-[10px] font-bold text-teal-700">{t("default_addr")}</span>
                )}
                <span className="ml-auto flex gap-1">
                  <button
                    data-testid={`address-edit-${a.id}`}
                    onClick={() => { setEditing(a); setOpen(true); }}
                    className="rounded-lg p-1.5 text-slate-500 hover:bg-teal-50 hover:text-teal-700"
                    aria-label="Edit address"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button
                    data-testid={`address-delete-${a.id}`}
                    onClick={() => del(a)}
                    className="rounded-lg p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600"
                    aria-label="Delete address"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </span>
              </div>
              <p className="mt-2 text-sm text-slate-600">{a.name} · {a.phone}</p>
              <p className="text-sm text-slate-600">
                {a.address}, {a.city}{a.region ? `, ${a.region}` : ""}{a.country ? `, ${a.country}` : ""}
              </p>
            </div>
          ))}
        </div>
      )}
      <AddressDialog open={open} onOpenChange={setOpen} initial={editing} onSaved={load} />
    </SubPageShell>
  );
}
